import { Loader2 } from "lucide-react";

import type { MedicoFila } from "@/api/db/medico";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import { useMedicoDiscardJob, useMedicoRetryJob } from "@/hooks/use-medico";
import { MedicoLabels } from "@/lib/labels/medico";

import { MetaLine, QueueRow, QueueSection } from "./queue-section";

function JobErrorRow({ job }: { job: MedicoFila["jobsError"][number] }) {
	const retry = useMedicoRetryJob();
	const discard = useMedicoDiscardJob();

	return (
		<QueueRow>
			<div className="flex flex-col gap-1.5" data-component="job-error-row" data-job={job.id}>
				<div className="flex flex-wrap items-center gap-2">
					<span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
						{MedicoLabels.jobType[job.type]}
					</span>

					{job.type === "exam" && (
						<button
							type="button"
							disabled={retry.isPending}
							data-slot="retry"
							onClick={() => retry.mutate({ id: job.id })}
							className="inline-flex items-center gap-1 text-xs font-medium text-primary transition-colors hover:text-primary-hover focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50"
						>
							{retry.isPending && <Loader2 className="size-3 animate-spin" />}
							{retry.isPending && MedicoLabels.jobRetrying}
							{!retry.isPending && MedicoLabels.jobRetry}
						</button>
					)}

					{job.type !== "exam" && (
						<ConfirmationDialog
							slot="discard"
							tone="destructive"
							title={MedicoLabels.jobDiscardConfirm}
							pendingLabel={MedicoLabels.jobDiscarding}
							isPending={discard.isPending}
							onConfirm={() => discard.mutate({ id: job.id })}
							className="inline-flex items-center gap-1 text-xs font-medium text-destructive transition-colors hover:text-destructive/80 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50"
						>
							{MedicoLabels.jobDiscard}
						</ConfirmationDialog>
					)}
				</div>

				<MetaLine>
					<span className="font-mono">#{job.id}</span>

					<span>{MedicoLabels.jobAttempts(job.attempts)}</span>
				</MetaLine>

				{job.errorReason !== null && (
					<p data-slot="error-reason" className="text-xs text-destructive">
						{MedicoLabels.jobError(job.errorReason)}
					</p>
				)}

				{!!job.errorDetail && (
					<pre
						data-slot="error-detail"
						className="max-h-24 overflow-auto rounded-md border border-border bg-background px-2 py-1.5 font-mono text-xs leading-relaxed text-muted-foreground"
					>
						{job.errorDetail}
					</pre>
				)}
			</div>
		</QueueRow>
	);
}

export function JobErrors({ jobs }: { jobs: MedicoFila["jobsError"] }) {
	if (jobs.length === 0) {
		return null;
	}

	return (
		<QueueSection
			title={MedicoLabels.jobErrorsTitle}
			subtitle={MedicoLabels.jobErrorsSubtitle}
			tone="coral"
		>
			{jobs.map((job) => (
				<JobErrorRow key={job.id} job={job} />
			))}
		</QueueSection>
	);
}
